/**
 * DataBest — Main application shell.
 * Sidebar navigation (role-aware) + top bar with user menu.
 * Used inside all protected routes.
 */
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
    Building2,
    Users2,
    LayoutDashboard,
    LogOut,
    Menu,
    X,
    Home,
    Boxes,
    Shield,
    UserCircle2,
    ClipboardCheck,
    BookOpen,
    AlertOctagon,
    ChevronDown,
    History as HistoryIcon,
    FileText,
    Activity,
} from "lucide-react";
import useAuthStore from "@/store/authStore";
import useAlertsStore from "@/store/alertsStore";
import { DataBestLogoShieldNetwork } from "@/components/DataBestLogo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_GROUPS = [
    {
        label: "Scoring",
        items: [
            {
                to: "/dashboard",
                label: "Dashboard",
                icon: LayoutDashboard,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                    "caregiver",
                    "analyst",
                ],
            },
            {
                to: "/today",
                label: "Today",
                icon: ClipboardCheck,
                roles: ["supervisor", "caregiver"],
            },
            {
                to: "/backlog",
                label: "Backlog",
                icon: AlertOctagon,
                badgeKey: "critical",
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                    "caregiver",
                ],
            },
            {
                to: "/history",
                label: "History",
                icon: HistoryIcon,
                roles: ["caregiver", "supervisor"],
            },
        ],
    },
    {
        label: "Plans & Reports",
        items: [
            {
                to: "/individuals",
                label: "Individuals",
                icon: UserCircle2,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                    "caregiver",
                    "analyst",
                ],
            },
            {
                to: "/assignments",
                label: "Assignments",
                icon: Users2,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                    "analyst",
                ],
            },
            {
                to: "/reports",
                label: "Individual BIRP Report",
                icon: FileText,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                    "caregiver",
                ],
            },
            {
                to: "/reports/caregiver-performance",
                label: "Caregiver Performance",
                icon: Activity,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                    "analyst",
                    "caregiver",
                ],
            },
        ],
    },
    {
        label: "Settings",
        items: [
            {
                to: "/admin/companies",
                label: "Companies",
                icon: Building2,
                roles: ["system_admin", "company_admin"],
            },
            {
                to: "/admin/providers",
                label: "Providers",
                icon: Boxes,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "analyst",
                ],
            },
            {
                to: "/admin/group-homes",
                label: "Group Homes",
                icon: Home,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                    "analyst",
                ],
            },
            {
                to: "/admin/houses",
                label: "Houses",
                icon: Home,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                    "analyst",
                ],
            },
            {
                to: "/admin/dictionaries",
                label: "Dictionaries",
                icon: BookOpen,
                roles: ["system_admin", "company_admin", "provider_admin"],
            },
        ],
    },
    {
        label: "System",
        items: [
            {
                to: "/admin/users",
                label: "Users",
                icon: Users2,
                roles: [
                    "system_admin",
                    "company_admin",
                    "provider_admin",
                    "supervisor",
                ],
            },
        ],
    },
];

const slugify = (s) =>
    s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const AppLayout = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuthStore();
    const [mobileOpen, setMobileOpen] = useState(false);
    const alertsSummary = useAlertsStore((s) => s.summary);
    const refreshAlerts = useAlertsStore((s) => s.refresh);
    const resetAlerts = useAlertsStore((s) => s.reset);

    // Fetch on mount + poll every 60s + on window focus.
    // Imperative refreshes (after a Save) are triggered from feature components.
    useEffect(() => {
        refreshAlerts();
        const iv = setInterval(refreshAlerts, 60000);
        const onFocus = () => refreshAlerts();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(iv);
            window.removeEventListener("focus", onFocus);
        };
    }, [refreshAlerts]);

    const handleLogout = async () => {
        resetAlerts();
        await logout();
        navigate("/login", { replace: true });
    };

    const initials = `${user?.first_name?.[0] || ""}${
        user?.last_name?.[0] || ""
    }`.toUpperCase();

    const visibleGroups = NAV_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((i) => i.roles.includes(user?.role)),
    })).filter((g) => g.items.length > 0);

    return (
        <div className="flex min-h-[100dvh] app-shell-bg" data-testid="app-shell">
            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-white/5 app-sidebar-bg transition-transform md:translate-x-0 ${
                    mobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}
                data-testid="app-sidebar"
            >
                <div className="flex h-16 items-center justify-between border-b border-white/10 px-5">
                    <DataBestLogoShieldNetwork
                        size={44}
                        withWordmark
                        dark
                        variantId="shell"
                    />
                    <button
                        className="md:hidden"
                        onClick={() => setMobileOpen(false)}
                        aria-label="Close menu"
                    >
                        <X className="h-5 w-5 text-slate-300" />
                    </button>
                </div>

                <nav className="flex-1 space-y-2.5 overflow-y-auto px-4 py-3">
                    {visibleGroups.map((group) => (
                        <div
                            key={group.label}
                            data-testid={`nav-group-${slugify(group.label)}`}
                        >
                            <p className="px-3 pb-1 pt-0.5 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                {group.label}
                            </p>
                            <div className="space-y-0.5">
                                {group.items.map((item) => {
                                    const Icon = item.icon;
                                    const badgeValue = item.badgeKey
                                        ? alertsSummary[item.badgeKey] || 0
                                        : 0;
                                    return (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            end
                                            data-testid={`nav-${item.to
                                                .replace("/admin/", "")
                                                .replace("/", "")}`}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 rounded-xl px-3 py-1.5 text-sm font-medium transition-colors ${
                                                    isActive
                                                        ? "bg-white/10 text-white border-l-[3px] border-brand-green-500 pl-[calc(0.75rem-3px)] shadow-[0_4px_12px_rgba(0,176,80,0.15)]"
                                                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                                                }`
                                            }
                                            onClick={() => setMobileOpen(false)}
                                        >
                                            <Icon className="h-4 w-4" />
                                            <span className="flex-1">
                                                {item.to ===
                                                    "/reports/caregiver-performance" &&
                                                    user?.role === "caregiver"
                                                    ? "My Performance"
                                                    : item.label}
                                            </span>
                                            {badgeValue > 0 && (
                                                <span
                                                    data-testid={`nav-badge-${item.to.replace("/", "")}`}
                                                    className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-black text-white"
                                                >
                                                    {badgeValue > 99 ? "99+" : badgeValue}
                                                </span>
                                            )}
                                        </NavLink>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="border-t border-white/10 p-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-green-500 text-sm font-bold text-white shadow-[0_4px_12px_rgba(0,176,80,0.3)]">
                            {initials || "U"}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-white">
                                {user?.first_name} {user?.last_name}
                            </p>
                            <p className="truncate text-xs text-slate-400">
                                {user?.role}
                            </p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-500/20 hover:text-red-300"
                            title="Sign out"
                            aria-label="Sign out"
                            data-testid="logout-button"
                        >
                            <LogOut className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Backdrop for mobile drawer */}
            {mobileOpen && (
                <button
                    className="fixed inset-0 z-20 bg-slate-900/60 md:hidden"
                    aria-label="Close menu"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Main */}
            <div className="flex flex-1 flex-col md:pl-64">
                <header className="sticky top-0 z-10 flex h-16 items-center justify-between app-header-glass px-4 md:px-8">
                    <button
                        className="md:hidden"
                        onClick={() => setMobileOpen(true)}
                        aria-label="Open menu"
                        data-testid="open-menu-button"
                    >
                        <Menu className="h-5 w-5 text-slate-700" />
                    </button>
                    <div className="hidden items-center gap-2 rounded-full border border-white/40 bg-white/50 px-3 py-1 text-xs font-medium text-slate-700 backdrop-blur md:flex">
                        <Shield className="h-3.5 w-3.5 text-brand-green-600" />
                        HIPAA-ready · Fully audited
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                data-testid="user-menu-trigger"
                                className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 text-left transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500"
                            >
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-blue-700 text-xs font-bold text-white">
                                    {initials || "U"}
                                </div>
                                <span className="hidden text-sm font-semibold text-slate-800 sm:inline">
                                    {user?.first_name} {user?.last_name}
                                </span>
                                <ChevronDown className="h-4 w-4 text-slate-500" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="w-64"
                            data-testid="user-menu-content"
                        >
                            <DropdownMenuLabel className="font-normal">
                                <p className="text-sm font-semibold text-slate-900">
                                    {user?.first_name} {user?.last_name}
                                </p>
                                <p
                                    className="truncate text-xs text-slate-500"
                                    data-testid="user-menu-email"
                                >
                                    {user?.email}
                                </p>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onSelect={handleLogout}
                                className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700"
                                data-testid="user-menu-logout"
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                Cerrar sesión
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </header>
                <main className="flex-1 p-4 md:p-8" data-testid="app-main">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default AppLayout;
